import { Link } from "react-router-dom";
import {
  Phone, ArrowRight, Scale, Briefcase, Landmark, RotateCcw, GraduationCap, LifeBuoy, Gavel,
} from "lucide-react";
import { FIRM, TEAM } from "@/data/content";
import { SERVICE_GROUPS } from "@/data/servicesIA";
import { SITUATIONS } from "@/data/situations";
import { CATEGORIES } from "@/data/resources";
import { FadeIn, TestimonialBand, AccreditationStrip } from "@/components/Shared";

const GROUP_ICONS = { Scale, Briefcase, Landmark, RotateCcw, GraduationCap };

export default function Home() {
  const namita = TEAM[0];
  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy text-white">
        <img
          src={FIRM.heroImage}
          alt="London"
          className="absolute inset-0 size-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/70 via-brand-navy/60 to-brand-navy" />
        <div className="brand-container relative pb-24 pt-44 sm:pb-32 sm:pt-56">
          <FadeIn>
            <p className="brand-eyebrow-on-dark tracking-[0.3em]" data-testid="hero-eyebrow">
              {FIRM.accreditation}
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="mt-6 max-w-4xl text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
              Criminal Defence
              <br />
              Solicitors London
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-slate-300">
              Operating since the 1950s, we are specialists in defending serious and
              complex crime and fraud. We are market leaders for our expertise,
              bespoke approach and client care.
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                to="/contact"
                className="brand-cta-primary px-8 py-4 text-center tracking-[0.2em]"
                data-testid="hero-cta-contact"
              >
                Get in touch for expert legal advice
              </Link>
              <Link
                to="/how-we-help"
                className="brand-cta-secondary px-8 py-4 text-center tracking-[0.2em]"
                data-testid="hero-cta-how-we-help"
              >
                How we can help
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.4}>
            <a
              href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
              className="mt-10 inline-flex items-center gap-3 text-sm text-slate-300 transition-colors hover:text-white"
              data-testid="hero-emergency-phone"
            >
              <span className="flex size-10 items-center justify-center bg-white/10">
                <Phone className="size-4 text-brand-terracotta" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Office line
                </span>
                {FIRM.phone}
              </span>
            </a>
          </FadeIn>
        </div>
      </section>

      <AccreditationStrip />

      {/* Dual route chooser */}
      <section className="brand-panel-pale py-20 sm:py-28 lg:py-32" data-testid="route-chooser-section">
        <div className="brand-container">
          <FadeIn>
            <p className="brand-eyebrow">
              Find the right route for you
            </p>
            <h2 className="brand-heading mt-4 max-w-2xl text-3xl sm:text-4xl">
              Start with the offence — or start with what's happening to you.
            </h2>
          </FadeIn>
          <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Route 1 — Our Services */}
            <FadeIn delay={0.1} className="h-full">
              <div className="brand-card flex h-full flex-col p-8 sm:p-10" data-testid="route-services-panel">
                <div className="flex items-center gap-3">
                  <Gavel className="size-6 text-brand-gold" />
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Our Services
                  </p>
                </div>
                <h3 className="brand-heading mt-4 text-2xl">
                  Browse by offence
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  The legal-category view — detailed pages for every offence we defend.
                </p>
                <ul className="mt-6 flex-1 space-y-2">
                  {SERVICE_GROUPS.map((g) => {
                    const Icon = GROUP_ICONS[g.icon] || Scale;
                    return (
                      <li key={g.slug}>
                        <Link
                          to={`/services/${g.slug}`}
                          className="group flex items-center justify-between border border-slate-200 bg-brand-ivory px-4 py-3.5 text-sm font-medium text-slate-700 transition-all duration-200 hover:border-brand-gold/60 hover:bg-white hover:text-brand-navy"
                          data-testid={`home-group-link-${g.slug}`}
                        >
                          <span className="flex items-center gap-3">
                            <Icon className="size-4 text-brand-gold" />
                            {g.title}
                          </span>
                          <ArrowRight className="size-4 text-brand-gold opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/services"
                  className="brand-text-link mt-6 inline-flex items-center gap-2"
                  data-testid="home-all-services-link"
                >
                  All services <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </FadeIn>

            {/* Route 2 — How We Help */}
            <FadeIn delay={0.2} className="h-full">
              <div className="brand-panel-dark flex h-full flex-col p-8 sm:p-10" data-testid="route-situations-panel">
                <div className="flex items-center gap-3">
                  <LifeBuoy className="size-6 text-brand-terracotta" />
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    How We Help
                  </p>
                </div>
                <h3 className="mt-4 text-2xl font-light tracking-tight">
                  Find help by situation
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  The plain-English view — short pages that reassure, then point you
                  to the detailed advice you need.
                </p>
                <ul className="mt-6 flex-1 space-y-1.5">
                  {SITUATIONS.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/how-we-help/${s.slug}`}
                        className="group flex items-center justify-between rounded px-3 py-2.5 text-sm text-slate-200 transition-colors duration-200 hover:bg-white/5 hover:text-white"
                        data-testid={`home-situation-link-${s.slug}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`size-1.5 ${s.urgent ? "bg-brand-terracotta" : "bg-brand-gold"}`} />
                          {s.title}
                        </span>
                        <ArrowRight className="size-4 text-brand-gold opacity-0 transition-opacity group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/how-we-help"
                  className="brand-text-link mt-6 inline-flex items-center gap-2"
                  data-testid="home-all-situations-link"
                >
                  How we help <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <TestimonialBand />

      {/* Team teaser */}
      <section className="bg-white py-20 sm:py-28 lg:py-32" data-testid="team-teaser-section">
        <div className="brand-container">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <FadeIn className="lg:col-span-5">
              <img
                src={namita.image}
                alt={namita.name}
                className="w-full rounded-sm border border-slate-200 object-cover md:rounded"
              />
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7">
              <p className="brand-eyebrow">
                Our team of criminal defence solicitors
              </p>
              <h2 className="brand-heading mt-4 text-3xl sm:text-4xl">
                Over 75 years combined experience, here to protect your rights.
              </h2>
              <div className="mt-8 rounded-sm border-l-2 border-brand-gold bg-brand-pale p-12 md:rounded">
                <p className="text-lg font-medium text-slate-800">{namita.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{namita.role}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{namita.short}</p>
                <Link
                  to={`/team/${namita.slug}`}
                  className="brand-text-link mt-4 inline-flex items-center gap-2"
                  data-testid="home-namita-profile-link"
                >
                  View profile <ArrowRight className="size-3.5" />
                </Link>
              </div>
              <Link
                to="/team"
                className="mt-8 inline-block rounded-sm border border-brand-navy px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-brand-navy transition-colors duration-200 hover:bg-brand-navy hover:text-white"
                data-testid="home-meet-team-button"
              >
                Meet the team
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Proposition + stats */}
      <section className="bg-white py-20 sm:py-28 lg:py-32" data-testid="proposition-section">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <FadeIn className="lg:col-span-7">
              <p className="brand-eyebrow">
                Market leading criminal defence
              </p>
              <h2 className="brand-heading mt-4 text-3xl sm:text-4xl">
                Experts in handling even the most difficult cases — combining
                excellent judgment with unrivalled tenacity.
              </h2>
              <p className="mt-6 leading-relaxed text-slate-600">
                Our devotion to clients and tenacious hard work has gained us
                recognition as a The Times Best Law Firm 2026 and Leading Firm in
                both legal directories the Legal 500 and Chambers and Partners.
              </p>
              <p className="mt-4 leading-relaxed text-slate-600">
                Our client base includes high profile individuals and cases, however
                we pride ourselves on our low profile approach — providing bespoke
                and professional criminal defence services from our office in
                London, taking a discreet and empathetic approach.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/results"
                  className="rounded-sm border border-brand-navy px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-brand-navy transition-colors duration-200 hover:bg-brand-navy hover:text-white"
                  data-testid="home-results-link"
                >
                  Our results
                </Link>
                <Link
                  to="/accreditations"
                  className="brand-cta-secondary px-7 py-3.5"
                  data-testid="home-accreditations-link"
                >
                  Accreditations & rankings
                </Link>
              </div>
            </FadeIn>
            <div className="grid grid-cols-1 gap-6 self-center sm:grid-cols-3 lg:col-span-5 lg:grid-cols-1">
              {[
                { value: "1950s", label: "Defending clients since" },
                { value: "75+", label: "Years combined experience" },
                { value: "Supreme Court", label: "From Magistrates' Court to the" },
              ].map((s, i) => (
                <FadeIn key={i} delay={i * 0.1}>
                  <div className="brand-panel-pale border-l-2 border-brand-gold p-6">
                    <p className="text-3xl font-light tracking-tight text-brand-navy">{s.value}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-slate-500">{s.label}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Legal resources spotlight */}
      <section className="bg-white py-20 sm:py-28 lg:py-32" data-testid="resources-spotlight-section">
        <div className="brand-container">
          <FadeIn>
            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <div>
                <p className="brand-eyebrow">
                  Inform and empower
                </p>
                <h2 className="brand-heading mt-4 text-3xl sm:text-4xl">
                  Legal resources
                </h2>
                <p className="mt-4 max-w-2xl leading-relaxed text-slate-600">
                  Our legal advice and information is there to help you understand the
                  law, process and procedure across a wide variety of legal topics
                  including fraud, drugs offences, sexual offences and more.
                </p>
              </div>
              <Link
                to="/resources"
                className="brand-text-link flex shrink-0 items-center gap-2"
                data-testid="home-view-all-resources"
              >
                All resources <ArrowRight className="size-4" />
              </Link>
            </div>
          </FadeIn>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((cat, i) => (
              <FadeIn key={cat.slug} delay={i * 0.08} className="h-full">
                <Link
                  to={`/resources/${cat.slug}`}
                  className="brand-card group flex h-full flex-col"
                  data-testid={`home-resource-card-${cat.slug}`}
                >
                  <div className="aspect-[16/8] overflow-hidden bg-brand-navy">
                    <img
                      src={cat.image}
                      alt={cat.title}
                      className="size-full object-cover opacity-80 transition-transform duration-300 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-brand-navy">
                      {cat.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}
